// app/(app)/employees/add.tsx
import { Button, Input } from "@/components/default/forms";
import { useAppTheme } from "@/hooks/useAppTheme";
import { generateEmployeeCode } from "@/services/employeesApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { clearError, createEmployee } from "@/store/slices/employeeSlice";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

export default function AddEmployeeScreen() {
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { updating, error } = useAppSelector((state) => state.employees);

  // Form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [designation, setDesignation] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    designation: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  // Reset form when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      // Reset all form fields
      setName("");
      setEmail("");
      setDesignation("");
      setPhone("");
      setPassword("");
      setConfirmPassword("");
      setErrors({
        name: "",
        email: "",
        designation: "",
        phone: "",
        password: "",
        confirmPassword: "",
      });
      dispatch(clearError());
    }, []),
  );

  // Show API errors
  useEffect(() => {
    if (error) {
      Alert.alert("Error", error);
      dispatch(clearError());
    }
  }, [error]);

  const validate = () => {
    let valid = true;
    const newErrors = {
      name: "",
      email: "",
      designation: "",
      phone: "",
      password: "",
      confirmPassword: "",
    };
    if (!name.trim()) {
      newErrors.name = "Name is required";
      valid = false;
    }
    if (!email.trim()) {
      newErrors.email = "Email is required";
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Invalid email";
      valid = false;
    }
    if (!designation.trim()) {
      newErrors.designation = "Designation is required";
      valid = false;
    }
    if (!phone.trim()) {
      newErrors.phone = "Phone is required";
      valid = false;
    } else if (!/^\d{10}$/.test(phone)) {
      newErrors.phone = "10-digit number required";
      valid = false;
    }
    if (!password) {
      newErrors.password = "Password is required";
      valid = false;
    } else if (password.length < 6) {
      newErrors.password = "At least 6 characters";
      valid = false;
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = "Confirm password";
      valid = false;
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      valid = false;
    }
    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const employeeCode = generateEmployeeCode();
    const result = await dispatch(
      createEmployee({
        ownerName: name,
        email,
        password,
        phone,
        designation,
        employeeCode,
      }),
    );
    if (createEmployee.fulfilled.match(result)) {
      Alert.alert("Success", "Employee added successfully", [
        { text: "OK", onPress: () => router.push("/(app)/employees") },
      ]);
    }
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    keyboardView: {
      flex: 1,
    },
    scrollContent: {
      padding: spacing.lg,
      paddingBottom: spacing.xl,
    },
    title: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.lg,
    },
    inputContainer: {
      marginBottom: spacing.lg,
    },
  });

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardView}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <Text style={styles.title}>Add Employee</Text>
          <Input
            label="Full Name"
            value={name}
            onChangeText={setName}
            error={errors.name}
            containerStyle={styles.inputContainer}
          />
          <Input
            label="Email"
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            error={errors.email}
            containerStyle={styles.inputContainer}
          />
          <Input
            label="Designation"
            value={designation}
            onChangeText={setDesignation}
            placeholder="Software Engineer"
            error={errors.designation}
            containerStyle={styles.inputContainer}
          />
          <Input
            label="Phone Number"
            value={phone}
            onChangeText={setPhone}
            keyboardType="phone-pad"
            placeholder="9876543210"
            error={errors.phone}
            containerStyle={styles.inputContainer}
          />
          <Input
            label="Password"
            value={password}
            onChangeText={setPassword}
            secureTextEntry
            error={errors.password}
            containerStyle={styles.inputContainer}
          />
          <Input
            label="Confirm Password"
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            secureTextEntry
            error={errors.confirmPassword}
            containerStyle={styles.inputContainer}
          />
          <Button
            title="Save Employee"
            onPress={handleSubmit}
            loading={updating}
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}
